import { useEffect, useSyncExternalStore } from 'react';
import { newId } from './ids';

// Navigation with tabs. Each tab has its own path and back history; the address bar
// mirrors the active tab. Any screen can live in a tab (a note, a chat, the calendar…).

export interface Tab {
  id: string;
  path: string;
  title?: string;
  pinned?: boolean;
  /** previous paths in this tab, for Back */
  history: string[];
}

interface RouterState {
  tabs: Tab[];
  activeId: string;
}

export interface Route {
  /** full path incl. query, e.g. "/notes/abc?focus=1" */
  path: string;
  pathname: string;
  /** "/notes/abc" → ["notes", "abc"] */
  segments: string[];
  query: URLSearchParams;
  tabId: string;
}

const KEY = 'ember.tabs';
export const HOME = '/today';

/** The start screen (Settings → Appearance → Start screen); notes by default. */
export function home(): string {
  try {
    const area = JSON.parse(localStorage.getItem('ember.startArea') ?? '"today"') as string;
    return area ? `/${area}` : HOME;
  } catch {
    return HOME;
  }
}
const listeners = new Set<() => void>();

function initialPath(): string {
  const p = location.pathname + location.search;
  return p === '/' || p === '' ? home() : p;
}

function load(): RouterState {
  const start = initialPath();
  try {
    // This window's own tabs survive reloads (sessionStorage); a fresh window starts from
    // the last tabs any window had, so two windows never fight over one set of tabs.
    const saved = JSON.parse(
      sessionStorage.getItem(KEY) ?? localStorage.getItem(KEY) ?? 'null',
    ) as RouterState | null;
    if (saved?.tabs?.length) {
      // A deep link (launcher icon, shortcut, pasted URL) opens in the active tab.
      const active = saved.tabs.find((t) => t.id === saved.activeId) ?? saved.tabs[0]!;
      const raw = location.pathname + location.search;
      // Opening the app at "/" restores the tabs as they were; any other address was asked for.
      if (raw !== '/' && raw !== '' && start !== active.path) {
        active.history = [...active.history.slice(-30), active.path];
        active.path = start;
        // The old title belonged to the old page; the new screen sets its own.
        active.title = undefined;
      }
      return { tabs: saved.tabs, activeId: active.id };
    }
  } catch {}
  const id = newId();
  return { tabs: [{ id, path: start, history: [] }], activeId: id };
}

let state: RouterState = load();

function save() {
  try {
    const json = JSON.stringify(state);
    sessionStorage.setItem(KEY, json);
    localStorage.setItem(KEY, json);
  } catch {}
}

function emit(push: boolean) {
  save();
  const path = activeTab().path;
  if (location.pathname + location.search !== path) {
    if (push) history.pushState(null, '', path);
    else history.replaceState(null, '', path);
  }
  for (const l of listeners) l();
}

function activeTab(): Tab {
  return state.tabs.find((t) => t.id === state.activeId) ?? state.tabs[0]!;
}

function setState(next: RouterState, push = true) {
  state = next;
  emit(push);
}

export interface NavigateOptions {
  replace?: boolean;
  /** open in a new tab */
  newTab?: boolean;
  /** with newTab: don't switch to it */
  background?: boolean;
}

export function navigate(path: string, opts: NavigateOptions = {}) {
  if (opts.newTab) {
    const tab: Tab = { id: newId(), path, history: [] };
    const idx = state.tabs.findIndex((t) => t.id === state.activeId);
    const tabs = [...state.tabs];
    tabs.splice(idx + 1, 0, tab);
    setState({ tabs, activeId: opts.background ? state.activeId : tab.id });
    return;
  }
  const cur = activeTab();
  if (cur.path === path) return;
  // Pinned tabs keep their place: navigating away from one opens a new tab instead.
  if (cur.pinned && !opts.replace && areaOf(path) !== areaOf(cur.path)) {
    navigate(path, { newTab: true });
    return;
  }
  const next: Tab = opts.replace
    ? { ...cur, path, title: undefined }
    : { ...cur, path, title: undefined, history: [...cur.history.slice(-50), cur.path] };
  setState({ ...state, tabs: state.tabs.map((t) => (t.id === cur.id ? next : t)) }, !opts.replace);
}

/** Back within the active tab. Returns false when there's nowhere to go. */
export function back(): boolean {
  const cur = activeTab();
  const prev = cur.history[cur.history.length - 1];
  if (prev === undefined) return false;
  const next: Tab = { ...cur, path: prev, title: undefined, history: cur.history.slice(0, -1) };
  setState({ ...state, tabs: state.tabs.map((t) => (t.id === cur.id ? next : t)) }, false);
  return true;
}

export function canGoBack(): boolean {
  return activeTab().history.length > 0;
}

export function activateTab(id: string) {
  if (state.activeId === id || !state.tabs.some((t) => t.id === id)) return;
  setState({ ...state, activeId: id });
}

export function closeTab(id: string) {
  const idx = state.tabs.findIndex((t) => t.id === id);
  if (idx < 0) return;
  let tabs = state.tabs.filter((t) => t.id !== id);
  let activeId = state.activeId;
  if (!tabs.length) {
    const tab: Tab = { id: newId(), path: home(), history: [] };
    tabs = [tab];
    activeId = tab.id;
  } else if (activeId === id) {
    activeId = (tabs[idx] ?? tabs[idx - 1] ?? tabs[0]!).id;
  }
  setState({ tabs, activeId });
}

export function closeOtherTabs(id: string) {
  setState({ tabs: state.tabs.filter((t) => t.id === id || t.pinned), activeId: id });
}

export function newTab(path = home()) {
  navigate(path, { newTab: true });
}

export function pinTab(id: string, pinned: boolean) {
  const tabs = state.tabs.map((t) => (t.id === id ? { ...t, pinned } : t));
  // Pinned tabs sit first.
  tabs.sort((a, b) => Number(!!b.pinned) - Number(!!a.pinned));
  setState({ ...state, tabs });
}

export function moveTab(from: number, to: number) {
  const tabs = [...state.tabs];
  const [t] = tabs.splice(from, 1);
  if (!t) return;
  tabs.splice(to, 0, t);
  setState({ ...state, tabs });
}

export function cycleTab(dir: 1 | -1) {
  const idx = state.tabs.findIndex((t) => t.id === state.activeId);
  const next = state.tabs[(idx + dir + state.tabs.length) % state.tabs.length];
  if (next) activateTab(next.id);
}

export function setTabTitle(id: string, title: string) {
  const t = state.tabs.find((x) => x.id === id);
  if (!t || t.title === title) return;
  state = { ...state, tabs: state.tabs.map((x) => (x.id === id ? { ...x, title } : x)) };
  save();
  for (const l of listeners) l();
}

/** Close tabs whose content no longer exists (e.g. a deleted note). */
export function closeTabsWhere(pred: (t: Tab) => boolean) {
  for (const t of state.tabs.filter(pred)) closeTab(t.id);
}

export function areaOf(path: string): string {
  return path.split(/[/?#]/).filter(Boolean)[0] ?? 'today';
}

export function parseRoute(path: string, tabId: string): Route {
  const [pathname = '/', search = ''] = path.split('?');
  return {
    path,
    pathname,
    segments: pathname.split('/').filter(Boolean).map(decodeURIComponent),
    query: new URLSearchParams(search),
    tabId,
  };
}

function subscribe(cb: () => void) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

export function useRouter(): RouterState {
  return useSyncExternalStore(subscribe, () => state);
}

let routeCache: { tab: Tab; route: Route } | null = null;
export function useRoute(): Route {
  return useSyncExternalStore(subscribe, () => {
    const tab = activeTab();
    if (routeCache?.tab.path === tab.path && routeCache.tab.id === tab.id) return routeCache.route;
    const route = parseRoute(tab.path, tab.id);
    routeCache = { tab, route };
    return route;
  });
}

export const getRoute = (): Route => parseRoute(activeTab().path, activeTab().id);

/** Sets the active tab's title from inside a screen. */
export function useTabTitle(title: string | undefined) {
  const route = useRoute();
  // navigate() clears a tab's title on every path change, so set it again then too.
  useEffect(() => {
    if (title) setTabTitle(route.tabId, title);
  }, [title, route.tabId, route.path]);
}

// Browser back/forward buttons.
window.addEventListener('popstate', () => {
  const path = location.pathname + location.search;
  const cur = activeTab();
  if (cur.history[cur.history.length - 1] === path) {
    back();
  } else if (cur.path !== path) {
    state = {
      ...state,
      tabs: state.tabs.map((t) =>
        t.id === cur.id ? { ...t, path: path === '/' ? home() : path, title: undefined } : t,
      ),
    };
    save();
    for (const l of listeners) l();
  }
});

// Ensure the address bar matches the restored tab on load.
history.replaceState(null, '', activeTab().path);
